import {
	TypographyCaps,
	TypographyHeading,
	TypographyParagraph,
} from '#app/components/typography'
import Button from '#app/components/buttons/button'
import Modal from '../modalsAndOverlays/modal'
import Image from '../content/Image'
import { useState } from 'react'
import sign from '#images/tutorial/sign.webp'
import search from '#images/tutorial/search.webp'
import template from '#images/tutorial/template.webp'
import Carousel from '../wayfinding/carousel'
import { CloseIcon } from '../icons'
import * as templates from '#app/utils/templates'

const templateArray = Object.values(templates)

const LoreTutorial = ({
	isOpen,
	onClose,
}: {
	isOpen: boolean
	onClose: () => void
}) => {
	const [step, setStep] = useState(0)
	const steps = [
		{
			title: 'Search Your NFT 🔎',
			body: (
				<div className="mt-6 text-left  text-textSecondaryLight dark:text-textSecondaryDark">
					<TypographyParagraph>
						Begin by searching for your NFT using its Marketplace URL, Contract
						address, or Token ID. Our platform supports{' '}
						<span className="text-abc font-paragraphMedium text-textPrimaryLight dark:text-textPrimaryDark">
							Ethereum, L2s (Base, Zora, Polygon, Shape), Tezos and Solana networks.
						</span>{' '}
						Once your NFT is imported, tap “Sign” to start adding context,
						history, social proof, or any relevant data by signing offchain
						information.
					</TypographyParagraph>
				</div>
			),
			image: search,
		},
		{
			title: 'Choose a template ☑️',
			body: (
				<div className="mt-6 text-left  text-textSecondaryLight dark:text-textSecondaryDark">
					<TypographyParagraph>
						Select from our pre-made templates to capture your NFT's offchain
						history:
					</TypographyParagraph>
					<ul className="list-disc pl-3">
						{templateArray.map(template => (
							<li key={template.slug}>
								<span className="text-abc font-paragraphMedium text-textPrimaryLight dark:text-textPrimaryDark">
									{template.name}:{' '}
								</span>{' '}
								{template.description}
							</li>
						))}
					</ul>
				</div>
			),
			image: template,
		},
		{
			title: 'Sign attestation ✍️',
			body: (
				<div className="mt-6 text-left  text-textSecondaryLight dark:text-textSecondaryDark">
					<TypographyParagraph>
						Fill out the template form and confirm the attestation information.
						Then, sign the message from your wallet provider to complete the
						transaction. We cover all gas fees! Once signed, your attestation
						will be stored in{' '}
						<span className="text-abc font-paragraphMedium text-textPrimaryLight dark:text-textPrimaryDark">
							Atomic Lore, IPFS, and Arweave.
						</span>{' '}
						You can view this information anytime by selecting “View Provenance”
						from the options menu.
					</TypographyParagraph>
				</div>
			),
			image: sign,
		},
	]
	return (
		<Modal
			size="large"
			title=""
			isOpen={isOpen}
			onClose={onClose}
			showClose={false}
			body={
				<div className="relative flex flex-col items-center">
					<div
						className="fixed right-4 top-4 cursor-pointer hover:opacity-80"
						onClick={() => onClose()}
					>
						<CloseIcon />
					</div>
					<TypographyCaps additionalClasses="text-uiBlue250">
						Step {step + 1}
					</TypographyCaps>
					<TypographyHeading
						size="large"
						isBold={true}
						additionalClasses={'mt-2 mb-6'}
					>
						{steps[step].title}
					</TypographyHeading>
					<Image
						className="h-[150px] w-full rounded-2xl"
						imageUrl={steps[step].image}
						imageAlt={steps[step].title}
					/>
					{steps[step].body}
					<Button
						additionalClasses="my-8 w-full"
						onClick={() => {
							if (step === 2) {
								onClose()
							} else {
								setStep(step + 1)
							}
						}}
					>
						{step === 2 ? 'Close' : 'Next'}
					</Button>
					<Carousel index={step} setIndex={setStep} count={steps.length} />
				</div>
			}
		/>
	)
}

export default LoreTutorial

const TutorialGlyphIcon = () => {
	return (
		<svg
			width="24"
			height="24"
			viewBox="0 0 24 24"
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
		>
			<path
				d="M11.5 8C11.2239 8 11 8.22386 11 8.5L11 11L8.5 11C8.22386 11 8 11.2239 8 11.5L8 12.5C8 12.7761 8.22386 13 8.5 13L11 13L11 15.5C11 15.7761 11.2239 16 11.5 16L12.5 16C12.7761 16 13 15.7761 13 15.5L13 13L15.5 13C15.7761 13 16 12.7761 16 12.5V11.5C16 11.2239 15.7761 11 15.5 11L13 11L13 8.5C13 8.22386 12.7761 8 12.5 8H11.5Z"
				fill="currentColor"
			/>
			<path
				fillRule="evenodd"
				clipRule="evenodd"
				d="M3 7V5H4V7H3ZM3 4V3H4V4H3ZM7 3H5V4H7V3ZM8 3V4H10V3H8ZM11 3V4H13V3H11ZM14 3V4H16V3H14ZM17 3V4H19V3H17ZM20 3V4L21 4V3H20ZM21 5H20V7H21V5ZM21 8H20V10H21V8ZM21 11H20V13H21V11ZM21 14H20V16H21V14ZM21 17H20V19H21V17ZM21 20H20V21H21V20ZM19 21V20H17V21H19ZM16 21V20H14V21H16ZM13 21V20H11V21H13ZM10 21V20H8V21H10ZM7 21V20H5V21H7ZM4 21V20H3V21H4ZM3 19H4V17H3V19ZM3 16H4V14H3V16ZM3 13H4V11H3V13ZM3 10H4V8H3V10Z"
				fill="currentColor"
			/>
		</svg>
	)
}
